import { useState, useEffect } from 'react';
import { Star, Quote } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        // Use default testimonials on error
        setTestimonials([
          {
            id: '1',
            name: 'Jennifer M.',
            location: 'Calgary, AB',
            rating: 5,
            text: 'YYC Custom transformed our unfinished basement into a beautiful family entertainment space. Their attention to detail and communication throughout the project was exceptional.',
            project_type: 'Basement Development',
          },
          {
            id: '2',
            name: 'Michael & Sarah T.',
            location: 'Airdrie, AB',
            rating: 5,
            text: 'Our bathroom renovation exceeded all expectations. The team was professional, clean, and delivered exactly what they promised. Highly recommend!',
            project_type: 'Bathroom Renovation',
          },
          {
            id: '3',
            name: 'David R.',
            location: 'Cochrane, AB',
            rating: 5,
            text: 'From the initial estimate to the final walkthrough, the entire process was smooth and transparent. Our legal basement suite was completed on time and on budget.',
            project_type: 'Legal Basement Suite',
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  return (
    <section className="py-24 md:py-32 bg-white" data-testid="testimonials-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#c5a059] text-sm uppercase tracking-[0.3em] mb-4">
            Client Testimonials
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-[#0f172a] mb-6">
            What Our Clients Say
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Don't just take our word for it. Here's what Calgary homeowners have to say 
            about their renovation experience with us.
          </p>
        </div>

        {/* Testimonials Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-[#f8f9fa] p-8 animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-24 mb-4" />
                <div className="h-24 bg-slate-200 rounded mb-4" />
                <div className="h-4 bg-slate-200 rounded w-32" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="testimonial-card relative"
                data-testid={`testimonial-${testimonial.id}`}
              >
                <Quote className="absolute top-6 right-6 text-[#c5a059]/20" size={48} />
                
                {/* Rating */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      className="fill-[#c5a059] text-[#c5a059]"
                    />
                  ))}
                </div>

                {/* Text */}
                <p className="text-slate-600 mb-6 leading-relaxed relative z-10">
                  "{testimonial.text}"
                </p>

                {/* Author */}
                <div className="border-t border-slate-200 pt-4">
                  <p className="font-semibold text-[#0f172a]">{testimonial.name}</p>
                  <p className="text-sm text-slate-500">
                    {testimonial.location} • {testimonial.project_type}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Trust Badges */}
        <div className="mt-16 pt-16 border-t border-slate-100">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div data-testid="trust-badge-projects">
              <p className="font-heading text-4xl font-bold text-[#0f172a]">500+</p>
              <p className="text-slate-500 text-sm uppercase tracking-wider mt-2">
                Projects Completed
              </p>
            </div>
            <div data-testid="trust-badge-experience">
              <p className="font-heading text-4xl font-bold text-[#0f172a]">15+</p>
              <p className="text-slate-500 text-sm uppercase tracking-wider mt-2">
                Years Experience
              </p>
            </div>
            <div data-testid="trust-badge-satisfaction">
              <p className="font-heading text-4xl font-bold text-[#0f172a]">100%</p>
              <p className="text-slate-500 text-sm uppercase tracking-wider mt-2">
                Satisfaction Rate
              </p>
            </div>
            <div data-testid="trust-badge-insured">
              <p className="font-heading text-4xl font-bold text-[#0f172a]">Fully</p>
              <p className="text-slate-500 text-sm uppercase tracking-wider mt-2">
                Licensed & Insured
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
